import React, { useContext, useMemo } from "react";
import { format } from "date-fns";

import { Context } from "../../../store/student";
import {
  CareerSheetComment,
  ICareerSheetIssue,
  ICareerSheet,
} from "../../../state";
import { ReactComponent as IconGood } from "../../../assets/images/Career/ChallengeSheet/icon_good.svg";
import styles from "../CareerAnswerHistory.module.scss";

const CareerSheetTable = () => {
  const { contextState } = useContext(Context);
  const {
    career_sheets: careerSheets,
    career_sheet_comments: careerSheetComments,
  } = contextState;

  const sortedCareerSheetsByTermNo = useMemo(
    () =>
      [...careerSheets]
        .filter(sheet => sheet.completed_at !== null)
        .sort((a, b) => b.term_no - a.term_no),
    [careerSheets],
  );

  const commentMap = useMemo(() => {
    const map = new Map<string, CareerSheetComment>();
    careerSheetComments.forEach(comment => {
      const key = `${comment.careergear_sheet_id}:${comment.careergear_sheet_question_id}`;
      map.set(key, comment);
    });
    return map;
  }, [careerSheetComments]);

  const sheetQuestions =
    careerSheets[0]?.careergear_sheet_issues?.flatMap(
      (issue: ICareerSheetIssue) => issue.careergear_sheet_questions,
    ) ?? [];

  const getAnswerContent = (sheet: ICareerSheet, questionId: number) => {
    const questions =
      sheet?.careergear_sheet_issues?.flatMap(
        (issue: ICareerSheetIssue) => issue.careergear_sheet_questions,
      ) ?? [];
    return (
      questions.find(q => q.id === questionId)?.careergear_sheet_answer
        ?.content ?? "—"
    );
  };

  const renderAnswerCell = (
    sheetId: number,
    questionId: number,
    answerContent: string,
  ) => {
    const comment = commentMap.get(`${sheetId}:${questionId}`);
    if (!comment?.marker || !answerContent.includes(comment.marker)) {
      return answerContent;
    }

    const index = answerContent.indexOf(comment.marker);
    return (
      <>
        {answerContent.slice(0, index)}
        <span className={styles.marker}>
          {comment.marker}
          <span className={styles.tooltip} role="tooltip">
            <IconGood className={styles.icon} />
            {comment.content}
          </span>
        </span>
        {answerContent.slice(index + comment.marker.length)}
      </>
    );
  };

  if (careerSheets.length === 0) {
    return <p className={styles.message}>回答データがありません。</p>;
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>問い</th>
          {sheetQuestions.map(question => (
            <th key={question.id}>{question.title}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedCareerSheetsByTermNo.map((sheet: ICareerSheet) => (
          <tr key={sheet.id}>
            <td>
              <div>{sheet.term_no}回目</div>
              <div className={styles.date}>
                {sheet.completed_at &&
                  format(new Date(sheet.completed_at), "yyyy.M.d")}
              </div>
            </td>
            {sheetQuestions.map(question => {
              const answerContent = getAnswerContent(sheet, question.id);
              return (
                <td key={`${sheet.id}-${question.id}`}>
                  {renderAnswerCell(sheet.id, question.id, answerContent)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CareerSheetTable;
